/*
 * Visitor time-format preference (12h vs 24h) for the public scheduling link.
 *
 * Source of truth, in priority order:
 *   1. localStorage override (the visitor's toggle choice, per-browser)
 *   2. the account owner's `Account#time_format_24h`, SSR'd onto <body data-time-format-24h>
 *
 * All localStorage access is guarded — Safari private mode and some iframe embed
 * contexts throw on access. We fall back to an in-memory cache so the toggle still
 * works for the session. This is a cosmetic preference, never a critical path.
 */
const KEY = 'simplero:booking:time-format-24h'

let memoryFallback = null
let lastDispatched = null

function readStorage() {
  try {
    return localStorage.getItem(KEY)
  } catch {
    return memoryFallback
  }
}

function writeStorage(value) {
  try {
    localStorage.setItem(KEY, value)
  } catch {
    memoryFallback = value
  }
}

export function accountDefaultIs24h() {
  // Read the raw attribute, not `dataset.timeFormat24h`: the `24h` segment
  // (a digit after a hyphen) does not camelCase into a clean dataset key in all
  // engines, so the dataset accessor can return undefined and silently fall
  // back to 12h. The attribute is always reliable.
  return document.body.getAttribute('data-time-format-24h') === 'true'
}

export function getIs24h() {
  const stored = readStorage()
  if (stored === '1') return true
  if (stored === '0') return false
  return accountDefaultIs24h()
}

export function setIs24h(is24h) {
  const value = is24h ? '1' : '0'
  // Dedupe identical writes so the two toggle instances reacting to the same
  // event can't ping-pong the switch back and forth.
  if (value === lastDispatched) return
  writeStorage(value)
  lastDispatched = value
  document.dispatchEvent(
    new CustomEvent('time-format:change', { detail: { is24h } })
  )
}

// Luxon token format for slot/event times (matches getTimeFormatFromLocale shape).
export function luxonTimeFormat() {
  return getIs24h() ? 'H:mm' : 'h:mm a'
}

// FullCalendar format object for axis labels and event times.
export function fullCalendarTimeFormat() {
  return getIs24h()
    ? {
        hour: '2-digit',
        minute: '2-digit',
        omitZeroMinute: true,
        hour12: false,
        meridiem: false,
      }
    : {
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: true,
        hour12: true,
        meridiem: 'lowercase',
      }
}
